<script setup lang="ts">
/**
 * ScenePaperPlane · 在线小剧场（变体）：窗台上扔纸飞机
 * ------------------------------------------------------------
 * 舞台 = 白天的窗（同 SceneOnline）：小家伙单手扶窗台，另一只手
 * 把纸飞机甩出去；飞机绕着整格舞台飞一圈又回到手上。
 * 人物比例与所有场景统一：头 r6.6、0.8px/viewBox 单位（见 scenes/index.ts）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" :class="{ hot }" aria-hidden="true">
    <span class="win">
      <span class="kid">
        <svg viewBox="0 0 20 26">
          <defs>
            <linearGradient id="apShirtPlane" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#7A95FF" />
              <stop offset="1" stop-color="#4C6BF0" />
            </linearGradient>
          </defs>
          <!-- 身体（连帽衫） -->
          <path d="M4.4 26v-5.4c0-2.7 2.4-4.3 5.6-4.3s5.6 1.6 5.6 4.3V26z" fill="url(#apShirtPlane)" />
          <path d="M7.3 16.6q2.7 2.1 5.4 0" stroke="#FFFFFF" stroke-opacity=".55" stroke-width=".9"
                fill="none" stroke-linecap="round" />
          <!-- 左手扶窗台 -->
          <rect x="1.1" y="21.3" width="4.5" height="3.3" rx="1.65" fill="#FFDCB4" stroke="#E6B183" stroke-width=".8" />
          <!-- 右手举起来扔飞机（每 4s 往前甩一下，跟飞机起飞同步） -->
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="0 15.2 17.3;0 15.2 17.3;-26 15.2 17.3;4 15.2 17.3;0 15.2 17.3"
                              keyTimes="0;.72;.8;.9;1" dur="4s" repeatCount="indefinite" />
            <rect x="14.3" y="12.9" width="1.8" height="4.4" rx=".9" fill="#4C6BF0" />
            <circle cx="15.2" cy="13.2" r="1.05" fill="#FFDCB4" stroke="#E6B183" stroke-width=".5" />
          </g>
          <!-- 头（目光追随飞机，轻轻摇摆） -->
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="-3 10 15;3 10 15;-3 10 15" dur="4s" repeatCount="indefinite" />
            <circle cx="10" cy="8.4" r="6.6" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
            <path d="M3.5 7.9a6.5 6.5 0 0 1 13 0v.8H3.5z" fill="#3A2B21" />
            <path d="M9.5 2.6c1.1-1.6 2.7-1.9 3.5-1.4-1.3.4-2.1 1-2.5 1.8z" fill="#3A2B21" />
            <path d="M6.9 5.9q1.1-.7 2.2-.2M13.1 5.9q-1.1-.7-2.2-.2" stroke="#3A2B21" stroke-width=".7"
                  fill="none" stroke-linecap="round" opacity=".75" />
            <ellipse cx="7.9" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="3.6s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="12.1" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="3.6s" repeatCount="indefinite" />
            </ellipse>
            <circle cx="8.5" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <circle cx="12.7" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <ellipse cx="5.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <ellipse cx="14.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <path d="M8.6 11.6a1.5 1.5 0 0 0 2.8 0z" fill="#C05A5A" />
          </g>
        </svg>
      </span>
      <span class="mull" />
    </span>
    <!-- 纸飞机：飞出窗户，绕整格舞台兜一圈（48×50 舞台坐标系） -->
    <svg class="plane" viewBox="0 0 48 50">
      <path d="M0 0 L5 1.7 L0 3.4 L1 1.7 Z" fill="#FFFFFF" stroke="#C9D2DE" stroke-width=".5">
        <animateMotion dur="4s" repeatCount="indefinite" rotate="auto"
                       path="M27 21 C 38 10, 46 16, 42 26 C 39 34, 30 30, 24 34 C 16 39, 5 34, 8 24 C 10 16, 20 16, 27 21 Z" />
      </path>
    </svg>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 白天的窗：与 SceneOnline 同款 */
.win {
  position: absolute; left: 50%; bottom: 7px;
  width: 36px; height: 26px; margin-left: -18px;
  border: 2px solid #C3D3E8; border-radius: 8px;
  background:
    radial-gradient(circle at 76% 28%, #ffffff 0 2px, transparent 2.3px),
    radial-gradient(circle at 84% 40%, #ffffffb0 0 1.4px, transparent 1.7px),
    linear-gradient(180deg, #a9d6ff 0%, #dcf0ff 66%, #c4e3ff 100%);
  box-shadow: inset 0 -2px 3px #1a3a6614, 0 1px 2px #0b122012;
}
.mull {
  position: absolute; z-index: 3;
  left: 50%; top: 0; width: 2px; height: 100%; margin-left: -1px;
  background: #c3d3e8;
}

/* 小人站着看飞机，轻轻踮脚；悬停时更起劲 */
.kid {
  position: absolute; left: 2px; bottom: 0; z-index: 2;
  display: block; font-size: 0; transform-origin: 50% 100%;
  filter: drop-shadow(0 1px 1px #1a3a6626);
  animation: ppBob 2s ease-in-out infinite alternate;
}
.kid svg { display: block; width: 16px; height: 21px; }
.sc.hot .kid { animation-duration: .9s; }
@keyframes ppBob {
  from { transform: translateY(0); }
  to   { transform: translateY(-1.1px); }
}

/* 飞机压在窗框之上飞，不挡点击 */
.plane {
  position: absolute; inset: 0; z-index: 5;
  width: 48px; height: 50px; pointer-events: none;
}
</style>
